import React, { useState, useMemo, useCallback } from 'react';

/**
 * Reusable DataTable component with:
 * - Client-side search across specified fields
 * - Column sorting
 * - Pagination (with page-size selector: 10, 25, 50, 100, All)
 * - Export to Excel (CSV) and PDF (print window)
 *
 * Props:
 *  data          - Array of row objects
 *  columns       - Array of { key, label, sortable?, render?, exportValue?, export?, thClass?, tdClass? }
 *  searchFields  - Array of field keys to include in text search
 *  filename      - Base filename for exports (no extension)
 *  title         - Title shown in the PDF header
 *  loading       - Show spinner instead of table
 *  rowKey        - Function(row) => unique key; falls back to index
 *  children      - Optional JSX rendered between top controls and table (extra filters)
 */
const DataTable = ({
  data = [],
  columns = [],
  searchFields = [],
  filename = 'export',
  title = '',
  loading = false,
  rowKey,
  children,
}) => {
  const [search, setSearch]         = useState('');
  const [pageSize, setPageSize]     = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortKey, setSortKey]       = useState('');
  const [sortDir, setSortDir]       = useState('asc');

  const exportCols = useMemo(
    () => columns.filter(c => c.export !== false),
    [columns]
  );

  /* ── filtered + sorted data ────────────────────────────────────────── */
  const filtered = useMemo(() => {
    let result = [...data];

    if (search.trim()) {
      const s = search.toLowerCase().trim();
      result = result.filter(row =>
        searchFields.some(field =>
          String(row[field] ?? '').toLowerCase().includes(s)
        )
      );
    }

    if (sortKey) {
      result.sort((a, b) => {
        const av = a[sortKey] ?? '';
        const bv = b[sortKey] ?? '';
        const cmp = String(av).localeCompare(String(bv), undefined, {
          numeric: true,
          sensitivity: 'base',
        });
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }

    return result;
  }, [data, search, sortKey, sortDir, searchFields]);

  /* ── pagination ────────────────────────────────────────────────────── */
  const isAll      = pageSize === 'all';
  const total      = filtered.length;
  const totalPages = isAll ? 1 : Math.max(1, Math.ceil(total / Number(pageSize)));
  const safePage   = Math.min(currentPage, totalPages);
  const startIdx   = isAll ? 0 : (safePage - 1) * Number(pageSize);
  const endIdx     = isAll ? total : Math.min(startIdx + Number(pageSize), total);
  const paginated  = filtered.slice(startIdx, endIdx);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };

  const getPageNumbers = () => {
    const maxVisible = 5;
    let s = Math.max(1, safePage - 2);
    let e = Math.min(totalPages, s + maxVisible - 1);
    if (e - s < maxVisible - 1) s = Math.max(1, e - maxVisible + 1);
    const pages = [];
    for (let i = s; i <= e; i++) pages.push(i);
    return pages;
  };

  /* ── exports ───────────────────────────────────────────────────────── */
  const exportToExcel = useCallback(() => {
    const headers = exportCols.map(c => c.label);
    const rows = filtered.map(row =>
      exportCols.map(col => {
        const val = col.exportValue ? col.exportValue(row) : (row[col.key] ?? '');
        return String(val);
      })
    );
    const csv = [headers, ...rows]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\r\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${filename}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [filtered, exportCols, filename]);

  const exportToPDF = useCallback(() => {
    const win = window.open('', '_blank');
    if (!win) return;

    const tableRows = filtered.map((row, ri) =>
      `<tr style="background:${ri % 2 === 0 ? '#ffffff' : '#f9fafb'}">
        ${exportCols.map(col => {
          const val = col.exportValue ? col.exportValue(row) : (row[col.key] ?? '');
          return `<td>${String(val).replace(/</g, '&lt;').replace(/>/g, '&gt;')}</td>`;
        }).join('')}
      </tr>`
    ).join('');

    win.document.write(`<!DOCTYPE html><html lang="en"><head>
      <meta charset="UTF-8">
      <title>${title || filename}</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box}
        body{font-family:Arial,sans-serif;padding:20px;font-size:12px;color:#333}
        .hdr{border-bottom:2px solid #2563eb;padding-bottom:10px;margin-bottom:14px}
        .hdr h2{font-size:18px;color:#1e3a5f;margin-bottom:4px}
        .hdr p{font-size:11px;color:#6b7280}
        table{width:100%;border-collapse:collapse}
        thead th{background:#2563eb;color:#fff;padding:8px 10px;text-align:left;
                 font-size:10px;text-transform:uppercase;letter-spacing:.05em}
        tbody td{padding:7px 10px;border-bottom:1px solid #e5e7eb;font-size:11px;vertical-align:top}
        .ftr{margin-top:18px;text-align:center;font-size:10px;color:#9ca3af;
             padding-top:10px;border-top:1px solid #e5e7eb}
        @media print{body{padding:10px}@page{margin:.8cm}}
      </style>
    </head><body>
      <div class="hdr">
        <h2>${title || filename}</h2>
        <p>Generated: ${new Date().toLocaleString()} &bull; Total records: ${total}</p>
      </div>
      <table>
        <thead><tr>${exportCols.map(c => `<th>${c.label}</th>`).join('')}</tr></thead>
        <tbody>${tableRows || `<tr><td colspan="${exportCols.length}" style="text-align:center;padding:20px;color:#9ca3af">No records</td></tr>`}</tbody>
      </table>
      <div class="ftr">Healthmax Limited &mdash; Customer Report System</div>
    </body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 600);
  }, [filtered, exportCols, filename, title, total]);

  /* ── helpers ───────────────────────────────────────────────────────── */
  const pageBtnCls = (active, disabled) =>
    `inline-flex items-center px-2.5 py-1 text-xs font-medium rounded border transition-colors duration-150 ${
      active
        ? 'bg-primary-600 border-primary-600 text-white'
        : disabled
          ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
    }`;

  /* ── render ────────────────────────────────────────────────────────── */
  return (
    <div className="space-y-4">

      {/* ── Top controls ─────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">

        {/* Show X entries */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-sm text-gray-600 whitespace-nowrap">Show</span>
          <select
            value={isAll ? 'all' : pageSize}
            onChange={e => {
              setPageSize(e.target.value === 'all' ? 'all' : Number(e.target.value));
              setCurrentPage(1);
            }}
            className="form-select text-sm py-1.5 w-[72px]"
          >
            {[10, 25, 50, 100].map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
            <option value="all">All</option>
          </select>
          <span className="text-sm text-gray-600 whitespace-nowrap">entries</span>
        </div>

        {/* Export + Search */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Export Excel */}
          <button
            onClick={exportToExcel}
            className="btn btn-secondary text-xs py-1.5 inline-flex items-center gap-1.5 flex-shrink-0"
            title="Export to Excel (CSV)"
          >
            <svg className="w-3.5 h-3.5 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Excel
          </button>

          {/* Export PDF */}
          <button
            onClick={exportToPDF}
            className="btn btn-secondary text-xs py-1.5 inline-flex items-center gap-1.5 flex-shrink-0"
            title="Export to PDF (Print)"
          >
            <svg className="w-3.5 h-3.5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            PDF
          </button>

          {/* Search box — grows to fill remaining space */}
          <div className="relative flex-1 min-w-[160px]">
            <div className="absolute inset-y-0 left-2.5 flex items-center pointer-events-none">
              <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              value={search}
              onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
              placeholder="Search..."
              className="form-input text-sm pl-8 pr-7 py-1.5 w-full"
            />
            {search && (
              <button
                onClick={() => { setSearch(''); setCurrentPage(1); }}
                className="absolute inset-y-0 right-2 flex items-center text-gray-400 hover:text-gray-600"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Extra filters slot ────────────────────────────────────────── */}
      {children && <div>{children}</div>}

      {/* ── Table ────────────────────────────────────────────────────── */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-16 bg-white">
            <div className="animate-spin h-8 w-8 border-4 border-primary-600 border-t-transparent rounded-full" />
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {columns.map(col => (
                  <th
                    key={col.key}
                    onClick={() => col.sortable !== false && handleSort(col.key)}
                    className={`px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap ${
                      col.sortable !== false
                        ? 'cursor-pointer select-none hover:bg-gray-100 active:bg-gray-200'
                        : ''
                    } ${col.thClass || ''}`}
                  >
                    <div className="flex items-center gap-1">
                      <span>{col.label}</span>
                      {col.sortable !== false && (
                        <span className="text-gray-400 text-sm leading-none">
                          {sortKey === col.key
                            ? sortDir === 'asc' ? '↑' : '↓'
                            : <span className="opacity-30">↕</span>}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-200">
              {paginated.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-4 py-12 text-center text-gray-500 text-sm"
                  >
                    {search
                      ? <span>No records match "<strong>{search}</strong>". Try a different search term.</span>
                      : 'No records found.'}
                  </td>
                </tr>
              ) : (
                paginated.map((row, i) => (
                  <tr
                    key={rowKey ? rowKey(row) : startIdx + i}
                    className="hover:bg-gray-50 transition-colors duration-100"
                  >
                    {columns.map(col => (
                      <td
                        key={col.key}
                        className={`px-4 py-3 text-sm text-gray-900 ${col.tdClass || ''}`}
                      >
                        {col.render
                          ? col.render(row[col.key], row, startIdx + i)
                          : (row[col.key] ?? '-')}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Bottom: info + pagination ─────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-gray-600">
          {loading
            ? 'Loading...'
            : total === 0
              ? 'No entries to show'
              : `Showing ${startIdx + 1}–${endIdx} of ${total} entries${
                  search && data.length !== total
                    ? ` (filtered from ${data.length} total)`
                    : ''
                }`}
        </p>

        {!isAll && totalPages > 1 && (
          <div className="flex items-center gap-1 flex-wrap">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={safePage === 1}
              className={pageBtnCls(false, safePage === 1)}
            >First</button>

            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={safePage === 1}
              className={pageBtnCls(false, safePage === 1)}
            >Prev</button>

            {getPageNumbers().map(n => (
              <button
                key={n}
                onClick={() => setCurrentPage(n)}
                className={pageBtnCls(n === safePage, false)}
              >{n}</button>
            ))}

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              className={pageBtnCls(false, safePage === totalPages)}
            >Next</button>

            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={safePage === totalPages}
              className={pageBtnCls(false, safePage === totalPages)}
            >Last</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataTable;
